<template>
  <CustomModal
    class="add-images-modal"
    :is-modal-open="isOpen"
    @toggleModal="() => emit('closeModal')"
    @onClickAway="() => emit('closeModal')"
    :overlay="'dark'"
    :with-header="true"
    :with-footer="true"
    :custom-classes="'tw:w-[100vw] tw:md:max-w-[636px] tw:md:w-[636px] tw:h-screen tw:md:h-auto tw:md:rounded-md tw:rounded-none'">
    <template #modalHeader>
      <p class="tw:text-xl tw:text-gray-800 tw:font-bold">{{ $t('nba_cta_expired_hotelier_images') }}</p>
    </template>

    <div class="tw:flex tw:flex-col tw:w-full">
      <div class="tw:flex tw:items-center tw:flex-col">
        <DropzoneUpload
          :files="files"
          :is-dragging="isDragging"
          @onChange="onFilesChange"
          @dragover="dragover"
          @drop="drop"
          @dragleave="dragleave"
          @remove="remove" />
      </div>
      <div class="tw:mt-6">
        <h4 class="tw:font-bold tw:text-sm">{{ $t('tbs_image_guidelines_header') }}</h4>
        <div class="tw:mt-3 tw:grid tw:grid-cols-1 tw:md:grid-cols-2 tw:gap-x-4 tw:gap-y-2">
          <div class="tw:space-y-2">
            <div v-for="(tip, index) in leftColumnTips" :key="`tip_left_${index}`" class="tw:text-sm">
              <span
                :class="tip.isPositive ? 'tw:text-blue-700' : 'tw:text-red-700'"
                class="tw:w-2 tw:inline-block tw:mr-1">
                {{ tip.isPositive ? '+ ' : '- ' }}
              </span>
              <span>{{ $t(tip.label) }}</span>
            </div>
          </div>
          <div class="tw:space-y-2">
            <div v-for="(tip, index) in rightColumnTips" :key="`tip_right_${index}`" class="tw:text-sm">
              <span
                :class="tip.isPositive ? 'tw:text-blue-700' : 'tw:text-red-700'"
                class="tw:w-2 tw:inline-block tw:mr-1">
                {{ tip.isPositive ? '+ ' : '- ' }}
              </span>
              <span>{{ $t(tip.label) }}</span>
            </div>
          </div>
        </div>
      </div>

      <div class="tw:mt-3">
        <button @click="toggleTips" class="tw:text-blue-700 tw:font-bold tw:text-sm tw:flex tw:items-center tw:gap-x-1">
          <span>{{ tipsOpened ? $t('tbs_images_hide_examples') : $t('tbs_images_show_examples') }}</span>
          <svg
            class="tw:w-[18px] tw:h-[18px] tw:text-blue-700 tw:transition-transform tw:duration-300"
            :class="{ 'tw:rotate-180': tipsOpened }"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="3" d="M19 9l-7 7-7-7" />
          </svg>
        </button>
      </div>
      <TipsContent v-if="tipsOpened" />
    </div>

    <template #modalFooter>
      <div class="tw:w-full tw:flex tw:justify-end tw:gap-4">
        <GhostButton size="medium" class="tw:px-9" @click="emit('closeModal')">{{ $t('close') }}</GhostButton>
        <MainButton size="medium" class="tw:px-9" :disabled="files.length === 0" @click="saveNewImages">
          {{ $t('nba_cta_expired_hotelier_images') }}
        </MainButton>
      </div>
    </template>
  </CustomModal>
</template>

<script setup lang="ts">
import axios from 'axios'
import { computed, ref, watch } from 'vue'
import { useQueryClient } from '@tanstack/vue-query'

import CustomModal from '@/components/base/CustomModal.vue'
import TipsContent from '@/components/propertyDetails/propertyImages/TipsContent.vue'
import DropzoneUpload from '@/components/propertyDetails/propertyImages/DropzoneUpload.vue'
import MainButton from '@/components/base/buttons/MainButton.vue'
import GhostButton from '@/components/base/buttons/GhostButton.vue'

import useToastNotifications from '@/components/notifications/useToastNotifications'
import apis from '@/services'
import appAnalytics from '@/utils/tracking'
import { generateGUID } from '@/utils/propertyDetailsHelpers.ts'
import { usePropertyImages, type UploadingImage } from '@/components/propertyDetails/propertyImages/usePropertyImages'
import { selectedAccommodationId } from '@/layouts/queries'

interface Props {
  isOpen: boolean
}
defineProps<Props>()

const emit = defineEmits<{
  closeModal: []
  openModal: []
}>()

const queryClient = useQueryClient()
const { isUploading, setUploadingImages, removeUploadingImages, setUploading } = usePropertyImages()
const openModal = () => emit('openModal')

const acceptedExtensions = ['.jpg', '.jpeg', '.png', '.webp']
const acceptedMimeTypes = ['image/jpeg', 'image/png', 'image/webp']
const maxFileSize = 20000000
const tipsOpened = ref(false)
const isDragging = ref(false)
const files = ref<File[]>([])
const uploadSucceed = ref(0)
const uploadFailed = ref<Array<'failed' | 'max_size' | 'min_size'>>([])
const tips = ref<{ label: string; isPositive: boolean }[]>([
  { label: 'image_guidelines_1', isPositive: true },
  { label: 'image_guidelines_2', isPositive: true },
  { label: 'image_guidelines_3', isPositive: true },
  { label: 'image_guidelines_4', isPositive: true },
  { label: 'images_guidelines_5', isPositive: false },
  { label: 'images_guidelines_6', isPositive: false },
])

const leftColumnTips = computed(() => {
  const mid = Math.ceil(tips.value.length / 2)
  return tips.value.slice(0, mid)
})

const rightColumnTips = computed(() => {
  const mid = Math.ceil(tips.value.length / 2)
  return tips.value.slice(mid)
})

const { displayNotification } = useToastNotifications()
const isValidFile = (file: File): boolean => {
  const fileName = file.name.toLowerCase()
  const hasValidExtension = acceptedExtensions.some(ext => fileName.endsWith(ext.toLowerCase()))
  const hasValidMimeType = acceptedMimeTypes.includes(file.type)

  if (!hasValidExtension && !hasValidMimeType) {
    return false
  }

  return true
}

const isDuplicateFile = (file: File, existingFiles: File[]): boolean => {
  return existingFiles.some(existingFile => existingFile.name === file.name && existingFile.size === file.size)
}

const validateAndCombineFiles = (newFiles: FileList): File[] => {
  const validFiles: File[] = []

  Array.from(newFiles).forEach(file => {
    if (!isValidFile(file)) {
      return
    }

    if (isDuplicateFile(file, files.value)) {
      return
    }

    validFiles.push(file)
  })

  return validFiles
}

const addFiles = (newFiles: FileList | null): void => {
  if (!newFiles || newFiles.length === 0) return

  const validFiles = validateAndCombineFiles(newFiles)
  files.value = [...files.value, ...validFiles]

  const filteredCount = newFiles.length - validFiles.length
  if (filteredCount > 0) {
    displayNotification({
      message: filteredCount === 1 ? 'app_images_upload_failed_image' : 'app_images_upload_failed_images',
      translationParams: { count: filteredCount },
      isTranslationKey: true,
      type: 'warning',
      isHtml: false,
    })
  }
}

watch(isUploading, (newValue, oldValue) => {
  if (newValue && !oldValue && uploadFailed.value.length === 0) {
    files.value = []
  }
})

const toggleTips = () => {
  tipsOpened.value = !tipsOpened.value
}
const onFilesChange = (e: Event) => {
  e.preventDefault()
  const input = e.target as HTMLInputElement
  addFiles(input.files)
  input.value = ''
}
const dragover = (e: DragEvent) => {
  e.preventDefault()
  isDragging.value = true
}
const dragleave = () => {
  isDragging.value = false
}
const drop = (e: DragEvent) => {
  e.preventDefault()
  isDragging.value = false
  addFiles(e.dataTransfer?.files ?? null)
}
const remove = (i: number) => {
  files.value.splice(i, 1)
}
const removeFile = (file: File) => {
  const index = files.value.indexOf(file)
  if (index !== -1) {
    files.value.splice(index, 1)
  }
}
const saveErrorCallback = () => {
  uploadFailed.value.push('failed')
}
const saveSuccessCallback = (file: File) => {
  uploadSucceed.value += 1
  removeFile(file)
}

const MAX_RETRY_ATTEMPTS = 3
const RETRY_JITTER_MAX_MS = 10_000

const uploadWithRetry = async (file: File, uploadingImage: UploadingImage, attemptsLeft = MAX_RETRY_ATTEMPTS) => {
  if (!selectedAccommodationId.value) {
    saveErrorCallback()
    return
  }
  try {
    const body = new window.FormData()
    body.append('image', file)
    await apis.accommodation.uploadImage({ accommodationId: selectedAccommodationId.value, body })
    saveSuccessCallback(file)
  } catch (error: unknown) {
    if (axios.isAxiosError(error) && error.response?.status === 429 && attemptsLeft > 0) {
      const retryAfterHeader = error.response.headers?.['retry-after']
      let retryAfterSecs = parseInt(retryAfterHeader, 10)
      if (isNaN(retryAfterSecs)) {
        const date = new Date(retryAfterHeader)
        retryAfterSecs = isNaN(date.getTime()) ? 30 : Math.max(0, Math.ceil((date.getTime() - Date.now()) / 1000))
      }
      const jitterMs = Math.random() * RETRY_JITTER_MAX_MS
      await new Promise(resolve => setTimeout(resolve, retryAfterSecs * 1000 + jitterMs))
      return uploadWithRetry(file, uploadingImage, attemptsLeft - 1)
    } else {
      saveErrorCallback()
    }
  }
}

const saveNewImages = () => {
  const promises: Promise<void>[] = []
  files.value.forEach(file => {
    promises.push(
      new Promise((resolve, reject) => {
        if (file.size > maxFileSize) {
          // prevent images > 20 MB from being uploaded
          uploadFailed.value.push('max_size')
          removeFile(file)
          resolve()
          return
        }
        const imageURL = URL.createObjectURL(file)
        let img = new Image()
        img.src = imageURL
        img.onload = () => {
          setUploading(true)
          if (img.width >= 2000) {
            const uploadingImage: UploadingImage = {
              id: generateGUID(),
              status: 'UPLOADING',
              name: file.name,
            }
            setUploadingImages(uploadingImage)
            emit('closeModal')
            uploadWithRetry(file, uploadingImage).then(resolve).catch(reject)
          } else {
            uploadFailed.value.push('min_size')
            removeFile(file)
            resolve()
          }
        }
      })
    )
  })

  Promise.allSettled(promises).then(() => {
    queryClient.invalidateQueries({ queryKey: ['images'] })
    removeUploadingImages()
    emit('closeModal')
    if (uploadSucceed.value > 0) {
      appAnalytics.track(appAnalytics.events.PD_NEW_IMAGES_UPLOADED, {
        item_id: selectedAccommodationId.value,
      })
      displayNotification({
        message: uploadSucceed.value > 1 ? 'app_images_uploaded_images' : 'app_images_uploaded_image',
        translationParams: { count: uploadSucceed.value },
        isTranslationKey: true,
        type: 'success',
        isHtml: false,
      })
      uploadSucceed.value = 0
    }

    if (uploadFailed.value.length > 0) {
      if (uploadFailed.value.every(item => item === 'min_size')) {
        displayNotification({
          message:
            uploadFailed.value.length > 1
              ? 'app_images_upload_failed_min_size_multiple'
              : 'app_images_upload_failed_min_size',
          translationParams: { count: uploadSucceed.value },
          isTranslationKey: true,
          type: 'error',
          isHtml: false,
        })
      } else if (uploadFailed.value.every(item => item === 'max_size')) {
        displayNotification({
          message:
            uploadFailed.value.length > 1
              ? 'app_images_upload_failed_max_size_multiple'
              : 'app_images_upload_failed_max_size',
          translationParams: { count: uploadSucceed.value },
          isTranslationKey: true,
          type: 'error',
          isHtml: false,
        })
      } else {
        displayNotification({
          message: uploadFailed.value.length > 1 ? 'app_images_upload_failed_images' : 'app_images_upload_failed_image',
          translationParams: { count: uploadFailed.value.length },
          isTranslationKey: true,
          type: 'error',
          isHtml: false,
          actionButton: true,
          actionLabel: 'dashboard.loadFail.retryButton',
          action: openModal,
        })
      }
      uploadFailed.value = []
      setUploading(false)
    }
  })
}

</script>
